<template>
  <div class="searchbar shadow">
    <b-navbar type="dark" variant="white" class="p-0 rounded-sm">
      <b-navbar-nav class="w-100">
        <b-nav-form
          class="searchbar__nav d-block w-100"
          @submit.prevent="onSearch"
        >
          <b-row class="w-100 d-flex m-0">
            <b-col
              cols="12"
              sm="6"
              class="searchbar__nav__section d-flex align-items-center p-0"
            >
              <search-suggestion
                ref="search"
                v-model="filter.search"
                class="search-input"
                :icon="require('@/assets/icons/search.svg')"
                placeholder="What are you looking for today?"
              ></search-suggestion>
            </b-col>
            <b-col
              cols="8"
              sm="2"
              class="searchbar__nav__section d-flex align-items-center p-0"
            >
              <place-autocomplete
                v-model="filter.location"
                :location="location"
                placeholder="Location"
                name="location"
                class="d-flex search-input"
                :api-key="$env.placesApiKey"
                :input-state="null"
                :input-error="null"
                input-class="ellipsis w-100"
                :prepend-icon="true"
                @select="setLocation"
              ></place-autocomplete>
            </b-col>

            <b-col
              cols="4"
              sm="2"
              class="searchbar__nav__section d-flex align-items-center p-0"
            >
              <b-nav-item-dropdown
                class="dropdown--primary"
                block
                link
                left
                no-caret
              >
                <template v-slot:button-content>
                  <b-input
                    v-model="filter.distance"
                    placeholder="0 km"
                    :class="[
                      'input__distance form-control--plain',
                      {
                        'input__distance--empty': filter.distance === '0 km'
                      }
                    ]"
                  ></b-input>
                  <b-icon
                    class="icon icon--filter"
                    icon="caret-down-fill"
                    aria-hidden="true"
                  ></b-icon>
                </template>
                <b-dropdown-item
                  v-for="distance in distances"
                  :key="distance.value"
                  @click="setDistance(distance)"
                  >{{ distance.text }}</b-dropdown-item
                >
              </b-nav-item-dropdown>
            </b-col>

            <b-col cols="12" sm="2" md="2" class="p-0">
              <b-button type="submit" variant="primary" block>
                Search
              </b-button>
            </b-col>
          </b-row>
        </b-nav-form>
      </b-navbar-nav>
    </b-navbar>
  </div>
</template>
<script>
/**
 * ==================================================================================
 * Searchbar component
 * ==================================================================================
 **/

import { findIndex } from 'lodash'
import PlaceAutocomplete from '@/components/autocomplete/PlaceAutocomplete'
import SearchSuggestion from '@/components/inputs/SearchSuggestion'

export default {
  components: {
    PlaceAutocomplete,
    SearchSuggestion
  },
  data: function() {
    return {
      location: null,
      distances: [
        { value: 1, text: '+1 km' },
        { value: 5, text: '+5 km' },
        { value: 10, text: '+10 km' },
        { value: 20, text: '+20 km' },
        { value: 50, text: '+50 km' }
      ],
      filter: {
        search: null,
        location: null,
        distance: '0 km',
        within_distance_to: null
      },
      coordinates: {
        lat: null,
        lng: null
      }
    }
  },

  methods: {
    init() {
      if (this.$route.params) {
        if (this.$route.params.filter) {
          this.filter = this.$route.params.filter
        }

        if (this.$route.params.coordinates) {
          this.coordinates = this.$route.params.coordinates
        }
      }
    },

    refresh() {
      this.$root.$emit('filterFeeds', this.filter)
    },

    onSearch() {
      if (this.$route.name === 'search') {
        this.refresh()
      } else {
        this.$router.push({
          name: 'search',
          params: {
            filter: this.filter,
            coordinates: this.coordinates
          }
        })
      }
    },

    setSearch() {
      this.refresh()
    },

    setLocation(coordinates) {
      this.location = coordinates

      if (coordinates) {
        this.coordinates.lat = coordinates.lat
        this.coordinates.lng = coordinates.lng

        const index = findIndex(this.distances, { text: this.filter.distance })
        let distance = null
        if (index >= 0) {
          distance = this.distances[index].value
        } else {
          this.filter.distance = this.distances[0].text
          distance = this.distances[0].value
        }

        this.filter.within_distance_to = this.getDistanceFilter(distance)
      } else {
        this.coordinates = {}
        this.filter.distance = null
        this.filter.within_distance_to = null
      }

      this.refresh()
    },

    setDistance(distance) {
      this.filter.distance = distance.text

      if (this.coordinates.lat && this.coordinates.lng) {
        this.filter.within_distance_to = this.getDistanceFilter(distance.value)
      } else {
        this.filter.within_distance_to = null
      }

      this.refresh()
    },

    getDistanceFilter(d) {
      return `${this.coordinates.lat},${this.coordinates.lng},${d * 1000}`
    },

    focus() {
      if (!this.$refs.search) return
      this.$refs.search.focus()
    }
  },

  mounted() {
    this.init()
  }
}
</script>
<style scoped lang="scss">
@import '@/assets/scss/abstracts/_mixins.scss';

.searchbar /deep/ {
  max-width: 1131px;

  $searchbar-color: #b5b5b5;

  input {
    width: 100%;
    height: 100%;
    font-size: 1.1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-top: 9px;
    padding-bottom: 9px;
    border-color: white;
    background: transparent;
    color: #000;

    @include placeholder {
      color: $searchbar-color;
    }
  }

  input:focus,
  input:active {
    border: 1px solid var(--brand-green);
    background: #f8fff9;
  }

  a {
    &:hover,
    &:focus {
      outline: none;
    }
  }

  button {
    width: 100%;
    height: 100%;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .icon {
    position: absolute;
    width: 15px;
    transition: transform var(--anim-trans);
    color: $searchbar-color;

    &--filter {
      top: 20px;
      right: 8px;
    }
  }

  .b-icon.bi {
    color: $searchbar-color;
  }

  .dropdown.show {
    .icon {
      transform: rotate(180deg);
    }
  }

  .search-suggestion,
  .autocomplete-field {
    .dropdown {
      position: absolute;
      top: 100%;
      left: 0;

      .dropdown-menu {
        border: 1px solid var(--brand-green);
      }
    }
  }

  .searchbar__nav {
    &__section {
      margin: 0;
      border-right: 1px solid var(--border-color);

      .search-icon {
        width: 25px;
        z-index: 1;
      }

      .search-input {
        width: 100%;
        height: 100%;
      }

      .input__distance {
        width: 100px;
        border: none !important;
      }

      .input__distance--empty {
        color: $searchbar-color;
      }
    }
  }
}

@media (max-width: 575px) {
  .searchbar /deep/ {
    button {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
    }

    .search-suggestion {
      border-bottom: 1px solid var(--border-color);

      &__img {
        left: 10px;
      }

      &__input {
        height: 58px;
        text-indent: 24.5px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    .autocomplete-field {
      border-right: 1px solid var(--border-color);

      input {
        border-radius: 0;
      }
    }

    .searchbar__nav__section {
      border-right: 0;

      .dropdown-menu {
        top: 100%;
      }

      .dropdown-toggle {
        border-radius: 0;
      }

      .input__distance {
        width: 100%;
      }
    }
  }
}
</style>
